<template>
  <section class="bg-gray-100 min-h-screen flex items-center justify-center">
    <div class="max-w-6xl mx-auto flex flex-col md:flex-row items-center px-4 md:px-6 space-y-10 md:space-y-0 md:space-x-10">
      
      <!-- Texto e CTA (Esquerda) -->
      <div class="w-full md:w-1/2 text-left flex flex-col justify-center h-auto  md:h-[600px] min-h-[450px]">
        <h1 class="text-2xl sm:text-3xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-4 sm:mb-6">
          {{ dynamicTitle }}
        </h1>
        <p class="mt-2 sm:mt-4 text-sm sm:text-base text-gray-600">
          {{ dynamicDescription }}
        </p>
        <div class="mt-6 sm:mt-8">
          <a
            href="#cta"
            class="bg-blue-600 text-white px-4 sm:px-6 py-3 rounded-lg shadow-md text-sm sm:text-base font-bold hover:bg-blue-700 transition"
          >
            Quero Mais Clientes Agora!
          </a>
        </div>
      </div>

      <!-- Simulação da Página Google (Direita) -->
      <div 
        class="w-full min-h-[600px] max-h-[600px] md:w-1/2 bg-white border border-gray-300 shadow-xl rounded-lg flex flex-col transition-all duration-500 ease-in-out"
        :class="{ 'min-h-[500px] sm:min-h-[600px]': isTyping || searchFinished }"
      >
        
        <!-- Barra do Navegador Simulada -->
        <div class="bg-gray-200 px-4 py-2 flex items-center space-x-2 sm:space-x-3 rounded-t-lg">
          <span class="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-red-500 rounded-full"></span>
          <span class="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-yellow-500 rounded-full"></span>
          <span class="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 rounded-full"></span>
          <div class="bg-white flex-1 rounded-full border border-gray-300 px-3 sm:px-4 py-1 text-gray-600 text-xs sm:text-sm truncate">
            🔍 Google.com/search
          </div>
        </div>

        <!-- Logo Google -->
        <div class="relative px-4 sm:px-6 mt-3 sm:mt-5 mb-5">
          <img src="/img/google.jpg" alt="Google" class="w-24 sm:w-32 mx-auto">
        </div>

        <!-- Campo de Busca Simulado -->
        <div class="relative px-4 sm:px-6 mt-2 sm:mt-2 mb-5">
          <div class="flex items-center bg-white border border-gray-300 rounded-full px-3 sm:px-4 py-2 shadow-md">
            <span class="text-gray-400 text-sm sm:text-base">🔍</span>
            <span v-if="!searchFinished || typedText" class="ml-2 sm:ml-3 text-sm sm:text-base text-gray-900 font-medium">
              {{ typedText }}<span class="cursor">|</span>
            </span>
          </div>
        </div>

        <!-- Resultados da Pesquisa -->
        <div v-if="searchFinished" class="px-4 sm:px-6 py-4 space-y-3 sm:space-y-4 overflow-y-auto flex-1">
          <p class="text-gray-500 text-xs">Resultados para: <strong>{{ typedText }}</strong></p>

          <!-- 🔸 Anúncio Patrocinado -->
          <div class="bg-white border border-yellow-300 rounded-lg p-2 sm:p-3 shadow-sm">
            <div class="text-xs text-gray-600">
              <span class="text-yellow-500 font-bold">Ad</span> · <span>{{ businessWebsite }}</span>
            </div>
            <a href="#" class="text-blue-700 text-sm sm:text-base font-semibold hover:underline break-words whitespace-normal">
              {{ typedText }} - {{ adsTitle }}
            </a>
            <p class="text-gray-700 text-xs sm:text-sm mt-1 break-words whitespace-normal">
              {{ typedText }}? {{ adsDescription }}
            </p>

            <a href="#" class="text-green-600 text-xs hover:underline">{{ businessWebsite }}/{{ typedText.replace(/\s+/g, "-").toLowerCase() }}</a>
          </div>

          <!-- 🌿 Resultados Orgânicos -->
          <div v-for="(result, index) in organicResults" :key="index" class="border-t border-gray-200 pt-3">
            <div class="text-xs text-gray-600 truncate">{{ result.url }}</div>
            <a href="#" class="text-blue-700 text-sm sm:text-base font-semibold hover:underline">
              {{ result.title }}
            </a>
            <p class="text-gray-700 text-xs sm:text-sm mt-1">
              {{ result.description }}
            </p>
          </div>

          <!-- Paginação Fake -->
          <div class="mt-3 sm:mt-4 flex justify-center space-x-2 text-blue-600 text-xs sm:text-sm">
            <span class="cursor-pointer hover:underline">1</span>
            <span class="cursor-pointer hover:underline">2</span>
            <span class="cursor-pointer hover:underline">3</span>
            <span class="cursor-pointer hover:underline">4</span>
            <span class="cursor-pointer hover:underline">Próximo »</span>
          </div>
        </div>
      </div>

    </div>
  </section>
</template>


<script setup>
import { ref, onMounted } from "vue";

const phrases = [
  "Clínica Odontológica em SP",
  "Marketing Digital para Empresas",
  "Tênis de Corrida Masculino",
  "Advogado Trabalhista em SP",
  "Melhores Restaurantes em Curitiba",
  "Curso de Inglês Online",
  "Montagem de Móveis Profissional",
  "Consultoria Financeira para Pequenas Empresas",
  "Melhor Celular de 2024",
  "Como ganhar dinheiro na internet"
];

// Anúncios Dinâmicos - Agora cada busca tem um anúncio correspondente
const adsList = {
  "Clínica Odontológica em SP": {
    title: "Agende sua consulta hoje!",
    description: "Atendimento especializado em implantes, clareamento e ortodontia."
  },
  "Marketing Digital para Empresas": {
    title: "Atraia mais clientes agora!",
    description: "Campanhas no Google Ads que geram resultados reais e aumentam suas vendas."
  },
  "Tênis de Corrida Masculino": {
    title: "O melhor tênis para correr!",
    description: "Frete grátis para todo o Brasil. Descontos especiais esta semana!"
  },
  "Advogado Trabalhista em SP": {
    title: "Assessoria Jurídica Especializada",
    description: "Proteja seus direitos! Consultoria trabalhista com profissionais experientes."
  },
  "Melhores Restaurantes em Curitiba": {
    title: "Experimente os melhores sabores!",
    description: "Confira os restaurantes mais bem avaliados e faça sua reserva online."
  },
  "Curso de Inglês Online": {
    title: "Aprenda Inglês do seu jeito!",
    description: "Aulas flexíveis e professores nativos. Comece hoje mesmo!"
  },
  "Montagem de Móveis Profissional": {
    title: "Montador de Móveis em sua região",
    description: "Serviço rápido e confiável. Agende um horário ainda hoje!"
  },
  "Consultoria Financeira para Pequenas Empresas": {
    title: "Finanças organizadas para crescer!",
    description: "Estratégias financeiras para aumentar sua lucratividade."
  },
  "Melhor Celular de 2024": {
    title: "Qual o melhor celular do ano?",
    description: "Veja comparativos e avaliações dos modelos mais avançados do mercado."
  },
  "Como ganhar dinheiro na internet": {
    title: "Descubra formas de renda online!",
    description: "Trabalhe de casa e aumente seus ganhos com estratégias comprovadas."
  }
};

// Resultados Orgânicos Simulados - Agora cada busca tem resultados específicos
const organicResultsList = {
  "Clínica Odontológica em SP": [
    { url: "www.dentistaespecialista.com.br", title: "Melhor Dentista em São Paulo", description: "Encontre especialistas em tratamentos dentários." },
    { url: "www.planoodontologico.com", title: "Planos Odontológicos com descontos", description: "Proteja seu sorriso pagando menos." }
  ],
  "Marketing Digital para Empresas": [
    { url: "www.agenciamarketing.com", title: "Aumente suas vendas com Google Ads", description: "Estratégias comprovadas para sua empresa crescer online." },
    { url: "www.seoconsultoria.com", title: "SEO e Marketing de Conteúdo", description: "Posicione sua empresa no topo do Google organicamente." }
  ],
  "Tênis de Corrida Masculino": [
    { url: "www.lojasport.com", title: "Os melhores tênis de corrida", description: "Confira nossa seleção de tênis confortáveis para atletas." },
    { url: "www.reviewstennis.com", title: "Avaliação dos melhores tênis para correr", description: "Compare preços e escolha o melhor modelo." }
  ],
  "Advogado Trabalhista em SP": [
    { url: "www.advocaciatrabalhista.com.br", title: "Defenda seus direitos trabalhistas", description: "Consulta gratuita para verificar sua situação legal." },
    { url: "www.escritoriojuridico.com", title: "Escritório de Advocacia em São Paulo", description: "Especialistas em rescisões e direitos do trabalhador." }
  ],
  "Melhores Restaurantes em Curitiba": [
    { url: "www.gastronomiacwb.com", title: "Os 10 melhores restaurantes de Curitiba", description: "Conheça os favoritos do público e faça sua reserva." },
    { url: "www.foodcuritiba.com", title: "Delivery e reservas online", description: "Peça sua comida favorita sem sair de casa." }
  ],
  "Curso de Inglês Online": [
    { url: "www.inglesexpress.com", title: "Aprenda Inglês rápido e fácil", description: "Cursos online com professores nativos." },
    { url: "www.fluencynaweb.com", title: "Inglês para negócios e viagens", description: "Método dinâmico e interativo para todas as idades." }
  ],
  "Montagem de Móveis Profissional": [
    { url: "www.montadorlocal.com", title: "Montadores de móveis em sua cidade", description: "Serviço profissional com garantia e agendamento fácil." },
    { url: "www.montamoveisexpress.com", title: "Montagem rápida e segura", description: "Peça seu orçamento agora mesmo!" }
  ],
  "Consultoria Financeira para Pequenas Empresas": [
    { url: "www.financassucesso.com", title: "Dicas financeiras para seu negócio", description: "Aumente seus lucros com planejamento estratégico." },
    { url: "www.gestorfinanceiro.com", title: "Gerenciamento financeiro para empreendedores", description: "Saiba como organizar as finanças da sua empresa." }
  ],
  "Melhor Celular de 2024": [
    { url: "www.tecnologiahoje.com", title: "Os celulares mais avançados do mercado", description: "Compare modelos e escolha o ideal para você." },
    { url: "www.smartphonesreview.com", title: "Qual celular vale mais a pena?", description: "Confira análises completas dos últimos lançamentos." }
  ],
  "Como ganhar dinheiro na internet": [
    { url: "www.trabalharemcasa.com", title: "As melhores formas de renda online", description: "Descubra como ganhar dinheiro sem sair de casa." },
    { url: "www.freelancerpro.com", title: "Freelancer: Trabalhe online e seja independente", description: "Veja como começar e conquistar clientes." }
  ]
};

// Títulos e descrições dinâmicos
const titles = [
  "Gestão de Google Ads que Gera Clientes Todos os Dias",
  "Maximize Seu Retorno com Gestão Profissional de Google Ads",
  "Especialistas em Google Ads: Mais Cliques, Mais Vendas, Mais Sucesso",
  "Quer Atrair Mais Clientes com Google Ads?"
];

const descriptions = [
  "Anúncios otimizados, mais cliques e mais vendas! Alcance clientes prontos para comprar com campanhas estratégicas no Google Ads.",
  "Pare de gastar e comece a vender mais! Criamos campanhas no Google Ads que convertem visitantes em clientes reais.",
  "Deixe seus concorrentes para trás! Criamos campanhas otimizadas no Google Ads para atrair clientes qualificados e escalar seu negócio.",
  "Campanhas personalizadas e tráfego qualificado! Nossa gestão de Google Ads coloca seu negócio na frente dos clientes certos, no momento certo."
];

const typedText = ref("");
const searchFinished = ref(false);
const businessWebsite = "www.seunegocio.com.br";
const adsTitle = ref("");
const adsDescription = ref("");
const organicResults = ref([]);
let phraseIndex = 0;
let charIndex = 0;
let isDeleting = false;
const dynamicTitle = ref("");
const dynamicDescription = ref("");

const updateDynamicContent = () => {
  const randomIndex = Math.floor(Math.random() * titles.length);
  dynamicTitle.value = titles[randomIndex];
  dynamicDescription.value = descriptions[randomIndex];
};

// Simulação da digitação automática e pesquisa
const typeEffect = () => {
  const currentPhrase = phrases[phraseIndex];

  if (charIndex < currentPhrase.length) {
    typedText.value += currentPhrase[charIndex++];
    setTimeout(typeEffect, 100);
  } else {
    searchFinished.value = true;
    updateResults();

    // Mantém os resultados visíveis por um tempo antes de limpar a busca
    setTimeout(() => {
      searchFinished.value = false;
      setTimeout(() => {
        typedText.value = "";
        phraseIndex = (phraseIndex + 1) % phrases.length;
        charIndex = 0;
        setTimeout(typeEffect, 1000); // Inicia a próxima busca
      }, 500); // Pequena pausa antes de limpar o texto
    }, 3000);
  }
};

// Atualiza os resultados com base na pesquisa simulada
// Atualiza os resultados com base na pesquisa simulada
const updateResults = () => {
  const currentPhrase = typedText.value;

  adsTitle.value = adsList[currentPhrase]?.title || "A melhor opção para você!";
  adsDescription.value = adsList[currentPhrase]?.description || "Encontre as melhores soluções aqui.";

  // Se houver resultados orgânicos específicos para a pesquisa, atualiza
  organicResults.value = organicResultsList[currentPhrase] || [
    { url: "www.resultadopadrao.com", title: `Informações sobre ${currentPhrase}`, description: "Descubra mais detalhes sobre este tema." },
    { url: "www.outrasfontes.com", title: `${currentPhrase} - Tudo que você precisa saber`, description: "Veja conteúdos relevantes sobre este assunto." }
  ];
};

// Inicia a animação da digitação automática
onMounted(() => {
  updateDynamicContent();
  setInterval(updateDynamicContent, 500000);
  setTimeout(typeEffect, 1000);
});
</script>

<style>
.break-words {
  word-break: break-word;
}

.whitespace-normal {
  white-space: normal;
}
</style>
